import { ELanguage } from '@if/core/types'
import {
  ActionButton,
  ActionMenuItem,
  Banner,
  Caption,
  ControlledAutocomplete,
  ControlledDateOnlyPicker,
  ControlledSelect,
  ControlledTextField,
  DataGrid,
  type DialogContentContext,
  FormContainer,
  type PaginationParams,
  useDialog,
  useIfUser,
} from '@if/mui'
import { Box, Chip, Stack } from '@mui/material'
import { useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { EInvitationDeliveryStatus, InvitationHistoryEntryDto } from '@src/types/InvitationHistoryDto'
import { ECommunicationType } from '@src/types/InvitationSchema'
import { Download, Search } from 'lucide-react'
import { type HTMLAttributes, type ReactNode, useCallback, useState } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  hasHistoryFilters,
  type InvitationHistoryFilterDraft,
  type InvitationHistoryFilters,
  invitationHistoryFilterSchema,
  isSameHistoryFilters,
  toHistoryQuery,
  useHistorySenders,
  useInvitationHistoryColumns,
  useInvitationHistoryData,
} from './invitationHistory-hooks'
import { toCsv } from './invitationHistoryCsv'

type HistoryState = ReturnType<typeof useInvitationHistoryState>
/** A picked entry plus the subsidiary it was read for, so a banner's Resend refills the right merchant. */
interface HistoryResult {
  entry: InvitationHistoryEntryDto
  merchantId: string | null
}
type HistoryCtx = DialogContentContext<void, HistoryResult, HistoryState>

// Invitations per page, not rows: a "both" send is two rows for one invitation. In a dialog the page
// size is effectively the body's height, and twenty-five left most of a screen of blanks under a short
// history. Ten fills it without a void.
const PAGE_SIZE = 10

const COMMUNICATION_TYPE_LABEL: Record<
  ECommunicationType,
  'invitationDialog.email' | 'invitationDialog.sms' | 'invitationDialog.emailAndSms'
> = {
  [ECommunicationType.Email]: 'invitationDialog.email',
  [ECommunicationType.SMS]: 'invitationDialog.sms',
  [ECommunicationType.Both]: 'invitationDialog.emailAndSms',
}

const useHistoryFilterSetup = () => ({ schema: invitationHistoryFilterSchema })

/**
 * Fixed control width in the filter row. It has to be set on a wrapper rather than the field: a
 * `Controlled*` fills its flex line, so five of them in a row is five full-width rows.
 */
const CONTROL_WIDTH = 170

type HistoryFilterMethods = UseFormReturn<InvitationHistoryFilterDraft, unknown, InvitationHistoryFilters>

interface ActiveFilterChip {
  key: string
  label: ReactNode
  onClear: () => void
}

interface FilterFieldsProps {
  methods: HistoryFilterMethods
  senders: [string, string][]
  onExport: () => void
  canExport: boolean
}

/**
 * The chips for whatever is currently narrowing the list, one per set field. They are the reason the
 * row can stay compact: a filter that is on says so here, so no control has to signal it by itself.
 */
const useActiveFilterChips = (
  values: Partial<InvitationHistoryFilterDraft>,
  senders: [string, string][],
  clear: (name: keyof InvitationHistoryFilterDraft) => void,
): ActiveFilterChip[] => {
  const { t } = useTranslation()
  const chips: ActiveFilterChip[] = []

  const push = (name: keyof InvitationHistoryFilterDraft, label: ReactNode) =>
    chips.push({ key: name, label, onClear: () => clear(name) })

  if (values.search) {
    push('search', values.search)
  }

  if (values.communicationType) {
    push('communicationType', t(COMMUNICATION_TYPE_LABEL[values.communicationType as ECommunicationType]))
  }

  if (values.deliveryStatus) {
    push('deliveryStatus', t(`invitationHistory.status.${values.deliveryStatus as EInvitationDeliveryStatus}`))
  }

  if (values.sentByUserId) {
    const sender = senders.find(([id]) => id === values.sentByUserId)
    push('sentByUserId', sender?.[1] ?? values.sentByUserId)
  }

  if (values.sentFrom) {
    push('sentFrom', `${t('invitationHistory.filters.sentFrom')} ${values.sentFrom.toString()}`)
  }

  if (values.sentTo) {
    push('sentTo', `${t('invitationHistory.filters.sentTo')} ${values.sentTo.toString()}`)
  }

  return chips
}

/**
 * The filter band, on the Messaging pattern: one bordered group holding the recipient search, a row
 * of compact controls under it, and the active filters as chips you can drop one at a time. The
 * controls are fixed-width so the row reads as a band rather than a stack of full-width fields.
 */
const HistoryFilterFields = ({ methods, senders, onExport, canExport }: FilterFieldsProps) => {
  const { t } = useTranslation()
  const { control, setValue, reset } = methods
  const values = useWatch({ control })

  const user = useIfUser()
  // Banner users read one subsidiary's history at a time; standard users have none.
  const [subsidiaries] = useMerchantByParentId(user.isBannerUser ? user.merchantId : '')
  const findSubsidiary = useCallback(
    (id: string | null) => subsidiaries?.find((m) => m.id === id),
    [subsidiaries],
  )

  const clear = (name: keyof InvitationHistoryFilterDraft) => {
    const empty = invitationHistoryFilterSchema.getDefault() as InvitationHistoryFilterDraft
    setValue(name, empty[name], { shouldDirty: true, shouldTouch: true })
  }

  const chips = useActiveFilterChips(values, senders, clear)

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack spacing={1.5}>
        {user.isBannerUser && (
          <ControlledAutocomplete
            control={control}
            name="merchantId"
            label={t('invitationHistory.filters.merchant')}
            options={(subsidiaries ?? []).map((m) => m.id)}
            getOptionKey={(id) => id ?? ''}
            getOptionLabel={(id) => findSubsidiary(id)?.name ?? ''}
            renderOption={(props, id) => {
              const { key, ...rest } = props as HTMLAttributes<HTMLLIElement> & { key: string }
              const merchant = findSubsidiary(id)
              return (
                <Box component="li" key={key} {...rest}>
                  <Stack direction="row" spacing={1}>
                    <span>{merchant?.name}</span>
                    {merchant && !merchant.isActive && <Chip size="small" label={t('accountStatus.inactive')} />}
                  </Stack>
                </Box>
              )
            }}
            disableClearable
          />
        )}
        <ControlledTextField
          control={control}
          name="search"
          label={t('invitationHistory.filters.recipient')}
          placeholder={t('invitationHistory.filters.searchPlaceholder')}
          startIcon={Search}
        />

        <Stack direction="row" spacing={1.5} alignItems="flex-end" flexWrap="wrap" useFlexGap>
          <Box sx={{ width: CONTROL_WIDTH }}>
            <ControlledSelect
              control={control}
              name="communicationType"
              label={t('invitationHistory.filters.channel')}
              emptyOptionLabel={t('invitationHistory.filters.all')}
              options={Object.values(ECommunicationType).map((value) => (
                <ActionMenuItem key={value} value={value}>
                  {t(COMMUNICATION_TYPE_LABEL[value])}
                </ActionMenuItem>
              ))}
            />
          </Box>
          <Box sx={{ width: CONTROL_WIDTH }}>
            <ControlledSelect
              control={control}
              name="deliveryStatus"
              label={t('invitationHistory.filters.status')}
              emptyOptionLabel={t('invitationHistory.filters.all')}
              options={Object.values(EInvitationDeliveryStatus).map((value) => (
                <ActionMenuItem key={value} value={value}>
                  {t(`invitationHistory.status.${value}`)}
                </ActionMenuItem>
              ))}
            />
          </Box>
          <Box sx={{ width: CONTROL_WIDTH }}>
            <ControlledSelect
              control={control}
              name="sentByUserId"
              label={t('invitationHistory.filters.sentBy')}
              emptyOptionLabel={t('invitationHistory.filters.all')}
              options={senders.map(([id, fullname]) => (
                <ActionMenuItem key={id} value={id}>
                  {fullname}
                </ActionMenuItem>
              ))}
            />
          </Box>
          <Box sx={{ width: CONTROL_WIDTH }}>
            <ControlledDateOnlyPicker
              control={control}
              name="sentFrom"
              label={t('invitationHistory.filters.sentFrom')}
            />
          </Box>
          <Box sx={{ width: CONTROL_WIDTH }}>
            <ControlledDateOnlyPicker control={control} name="sentTo" label={t('invitationHistory.filters.sentTo')} />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
            <ActionButton size="small" startIcon={Download} onClick={onExport} disabled={!canExport}>
              {t('invitationHistory.export')}
            </ActionButton>
          </Stack>
        </Stack>

        {chips.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Caption>{t('invitationHistory.filters.active')}</Caption>
            {chips.map((chip) => (
              <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onClear} />
            ))}
            <ActionButton size="small" onClick={() => reset(invitationHistoryFilterSchema.getDefault())}>
              {t('invitationHistory.filters.clear')}
            </ActionButton>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

/**
 * The dialog's own state. It runs inside the open dialog, so the toolbar's filters and the body's grid
 * read one state rather than two copies - which is what lets the filter band sit above a scrolling
 * body. Module scope, per the Rules of Hooks.
 */
const useInvitationHistoryState = () => {
  const [filters, setFilters] = useState<InvitationHistoryFilters>(() => invitationHistoryFilterSchema.getDefault())
  const [pagination, setPagination] = useState<PaginationParams>({ offset: 0, limit: PAGE_SIZE })

  // A narrowed filter set has to start from the first page, or the grid asks for an offset the new
  // result set may not reach and answers empty. Both are set together here rather than in an effect
  // keyed on `filters`, and only for a set that actually differs: the form re-submits a fresh object
  // on every debounced keystroke, so an effect would send the reader back to page one mid-read.
  const applyFilters = useCallback(
    (values: InvitationHistoryFilters) => {
      if (isSameHistoryFilters(filters, values)) {
        return
      }

      setFilters(values)
      setPagination((current) => ({ ...current, offset: 0 }))
    },
    [filters],
  )

  const user = useIfUser()
  const query = toHistoryQuery(filters, pagination)

  // A banner reads a chosen subsidiary, so hold the query until one is picked rather than firing it
  // against the banner's own merchant first; standard users always read their own.
  const enabled = !user.isBannerUser || !!query.merchantId
  const { invitations, rows, pageCount, hasMore, isLoading, isError } = useInvitationHistoryData(query, enabled)

  return {
    applyFilters,
    pagination,
    setPagination,
    query,
    rows,
    pageCount,
    hasMore,
    senders: useHistorySenders(invitations),
    isLoading,
    isError,
    // A banner that hasn't picked a subsidiary yet: nothing is queried, so the body invites a choice.
    awaitingMerchant: !enabled,
  }
}

/**
 * Previous / page / Next over the invitations, which is the unit offset and limit are in. The grid's
 * own pager cannot be used here: it reads the page length off the rows it was handed and a "both" send
 * is two of those for one invitation, so it would offer a next page that does not exist and label a
 * range wider than the page.
 */
const InvitationHistoryPager = ({ state, loading }: { state: HistoryState; loading: boolean }) => {
  const { t } = useTranslation()
  const { pagination, setPagination, pageCount, hasMore } = state
  const { offset, limit } = pagination

  if (offset === 0 && !hasMore) {
    return null
  }

  const goTo = (nextOffset: number) => setPagination({ offset: Math.max(0, nextOffset), limit })

  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
      {pageCount > 0 && (
        <Caption aria-live="polite">
          {t('invitationHistory.pager.range', { from: offset + 1, to: offset + pageCount })}
        </Caption>
      )}
      <ActionButton size="small" disabled={loading || offset === 0} onClick={() => goTo(offset - limit)}>
        {t('invitationHistory.pager.previous')}
      </ActionButton>
      <Caption>{Math.floor(offset / limit) + 1}</Caption>
      <ActionButton size="small" disabled={loading || !hasMore} onClick={() => goTo(offset + limit)}>
        {t('invitationHistory.pager.next')}
      </ActionButton>
    </Stack>
  )
}

/** The filters and Export, in the band that stays put while the rows scroll under it. */
const InvitationHistoryToolbar = ({ state }: { state: HistoryState }) => {
  const { t } = useTranslation()
  const { rows, senders, applyFilters } = state

  const handleExport = () => {
    const headers = [
      t('invitationHistory.columns.type'),
      t('invitationHistory.columns.recipient'),
      t('invitationHistory.columns.contact'),
      t('invitationHistory.columns.language'),
      t('invitationHistory.columns.sentAt'),
      t('invitationHistory.columns.sentBy'),
      t('invitationHistory.columns.status'),
    ]
    // Excel reads a UTF-8 CSV as the system codepage unless the file starts with a BOM, which mangles
    // every accent in the French column headers. Written as an escape rather than a literal character,
    // so no editor or tool can silently drop it.
    const blob = new Blob(['\ufeff', toCsv(rows, headers, (languageId) => ELanguage[languageId] ?? '')], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'invitation-history.csv'
    // Firefox ignores a programmatic click on an anchor that is not in the document.
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    // `filters` mode: every field reads as optional (no qualifier), and changes apply live, debounced.
    <FormContainer
      useFormSetup={useHistoryFilterSetup}
      mode="filters"
      hideActions
      onSubmit={(values) => applyFilters(values)}
    >
      {(methods) => (
        <HistoryFilterFields methods={methods} senders={senders} onExport={handleExport} canExport={rows.length > 0} />
      )}
    </FormContainer>
  )
}

const InvitationHistoryContent = ({ ctx }: { ctx: HistoryCtx }) => {
  const { t } = useTranslation()
  const { query, rows, isLoading, isError, awaitingMerchant } = ctx.state

  const user = useIfUser()
  const [subsidiaries] = useMerchantByParentId(user.isBannerUser ? user.merchantId : '')
  // Resend sends under the selected subsidiary, so an inactive one cannot be resent to (the backend
  // refuses it too); reading its history stays allowed.
  const resendDisabled =
    user.isBannerUser && !!query.merchantId && (subsidiaries ?? []).some((m) => m.id === query.merchantId && !m.isActive)

  // Picking a row closes the dialog and hands the entry back to the invite form, which refills itself
  // from it rather than sending straight away.
  const handleResend = useCallback(
    (entry: InvitationHistoryEntryDto) => ctx.close({ entry, merchantId: ctx.state.query.merchantId ?? null }),
    [ctx],
  )
  const columns = useInvitationHistoryColumns(handleResend, resendDisabled)

  return (
    <Stack spacing={2}>
      {isError && <Banner severity="error">{t('invitationHistory.loadError')}</Banner>}

      {awaitingMerchant ? (
        // A banner hasn't chosen a subsidiary yet: nothing is queried, so prompt for a choice rather
        // than showing an empty grid.
        <Banner severity="info">{t('invitationHistory.selectMerchant')}</Banner>
      ) : (
        <>
          {/* Unpaged: the page is already one server page of invitations, and the grid's pager counts
              rows rather than invitations. InvitationHistoryPager below pages in the right unit. */}
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            hasFilters={hasHistoryFilters(query)}
            dense
            paginated={false}
          />

          <InvitationHistoryPager state={ctx.state} loading={isLoading} />
        </>
      )}
    </Stack>
  )
}

/**
 * The invitation history dialog, opened from the invite form. It is a picker: the
 * dialog resolves with the history entry whose Resend was pressed (or `null` when
 * closed), and the invite form refills itself from that entry.
 */
export const useInvitationHistoryDialog = () => {
  const { t } = useTranslation()

  const dialog = useDialog<void, HistoryResult, HistoryState>({
    title: t('invitationHistory.title'),
    maxWidth: 'lg',
    fullWidth: true,
    // Nothing to confirm: a row's Resend closes the dialog with that entry.
    hideConfirm: true,
    cancelLabel: t('invitationHistory.close'),
    useDialogState: useInvitationHistoryState,
    // The filters govern which rows show, so they belong in the band that does not scroll - in the body
    // they slid out of view as soon as the reader moved past the first rows.
    toolbar: (ctx) => <InvitationHistoryToolbar state={ctx.state} />,
    content: (ctx) => <InvitationHistoryContent ctx={ctx} />,
  })

  return { open: () => dialog.open() }
}
